import { SponsorPlacement } from 'api';
import TextButton from 'components/TextButton';
import { useCampaignCreation } from 'pages/AdCampaignPage/CampaignCreationContext';
import PageHeading from 'pages/AdCampaignPage/PageHeading';
import { useCallback } from 'react';
import { useEventTracking } from 'state/eventTracking';
import { useIsProGrow } from 'state/pricing';
import { STEP_TITLES } from '../constants';
import {
  HAS_TARGETING_BY_PLACEMENT,
  PLACEMENTS_BY_CONTENT_TYPE,
  TRACKING_SELECTION_BY_PLACEMENT,
} from './constants';
import HeadlinerCard from './HeadlinerCard';
import PublisherNetworkCard from './PublisherNetworkCard';
import * as S from './styles';
import TargetingSelection from './TargetingSelection';
import { PlacementCardProps } from './types';
import YoutubeCard from './YoutubeCard';

const CARD_BY_PLACEMENT: Record<
  SponsorPlacement,
  React.FC<PlacementCardProps>
> = {
  headlinerApp: HeadlinerCard,
  publisherNetwork: PublisherNetworkCard,
  youtubePromotion: YoutubeCard,
};

export interface PlacementStepProps {
  onContinue: () => void;
}

const PlacementStep: React.FC<PlacementStepProps> = ({ onContinue }) => {
  const { trackPodcastPromoPurchaseNext } = useEventTracking();

  const {
    state: {
      subStep,
      values: { episode },
    },
    dispatch,
  } = useCampaignCreation();

  const { data: isProGrow } = useIsProGrow();

  const placementOptions = episode
    ? PLACEMENTS_BY_CONTENT_TYPE[episode.type]
    : [];

  const switchSubstep = useCallback((): void => {
    dispatch({
      type: 'GO_TO_STEP',
      payload: {
        step: 'placement',
        subStep: subStep === 'initial' ? 'targetSelection' : 'initial',
      },
    });
  }, [dispatch, subStep]);

  const createHandlePlacementSelection = useCallback(
    (placement: SponsorPlacement) => (): void => {
      trackPodcastPromoPurchaseNext({
        step: 'Placement',
        selection: TRACKING_SELECTION_BY_PLACEMENT[placement],
      });
      dispatch({
        type: 'CHANGE_PLACEMENT',
        payload: { placement, isProGrow },
      });

      if (HAS_TARGETING_BY_PLACEMENT[placement]) {
        switchSubstep();
      } else {
        onContinue();
      }
    },
    [
      dispatch,
      isProGrow,
      onContinue,
      switchSubstep,
      trackPodcastPromoPurchaseNext,
    ],
  );

  return (
    <>
      <PageHeading
        title={STEP_TITLES[subStep === 'initial' ? 'placement' : 'targeting']}
      >
        {subStep === 'targetSelection' && (
          <TextButton onPress={switchSubstep}>
            ← return to placement selection
          </TextButton>
        )}
      </PageHeading>

      {subStep === 'initial' && (
        <S.Root>
          {placementOptions.map(({ placement, isComingSoon }) => {
            const PlacementCard = CARD_BY_PLACEMENT[placement];

            return (
              <PlacementCard
                key={placement}
                isComingSoon={isComingSoon}
                onPress={createHandlePlacementSelection(placement)}
              />
            );
          })}
        </S.Root>
      )}

      {subStep === 'targetSelection' && (
        <TargetingSelection onContinue={onContinue} />
      )}
    </>
  );
};

export default PlacementStep;
